import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { UserProfile, UserStats } from '@/types';

export const useAdminStats = () => {
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers: 0,
    freeUsers: 0,
    proUsers: 0,
    userGrowth: 0,
    activeGrowth: 0,
    totalProjects: 0,
    totalWorkingHours: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // 全ユーザーを取得
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        let totalUsers = 0;
        let activeUsers = 0;
        let freeUsers = 0;
        let proUsers = 0;
        
        // 今月の開始日
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        
        let newUsersThisMonth = 0;
        let newUsersLastMonth = 0;
        
        usersSnapshot.forEach((doc) => {
          const user = doc.data() as UserProfile;
          totalUsers++;
          
          // プラン別集計
          if (user.plan === 'Free') {
            freeUsers++;
          } else if (user.plan === 'Standard') {
            proUsers++;
          }
          
          // 作成日による集計
          const createdAt = user.createdAt?.toDate();
          if (createdAt) {
            if (createdAt >= startOfMonth) {
              newUsersThisMonth++;
            } else if (createdAt >= lastMonth && createdAt < startOfMonth) {
              newUsersLastMonth++;
            }
          }
        });
        
        // アクティブユーザー（過去30日以内に稼働記録があるユーザー）
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        // 各ユーザーの稼働記録をチェック
        for (const userDoc of usersSnapshot.docs) {
          const timestampsRef = collection(db, `users/${userDoc.id}/timestamps`);
          const recentQuery = query(
            timestampsRef,
            where('clockInTime', '>=', Timestamp.fromDate(thirtyDaysAgo))
          );
          
          const timestampsSnapshot = await getDocs(recentQuery);
          if (!timestampsSnapshot.empty) {
            activeUsers++;
          }
        }
        
        // 成長率計算
        const userGrowth = newUsersLastMonth > 0
          ? ((newUsersThisMonth - newUsersLastMonth) / newUsersLastMonth) * 100
          : 0;
        
        // 総プロジェクト数と総稼働時間を集計
        let totalProjects = 0;
        let totalWorkingMinutes = 0;
        
        for (const userDoc of usersSnapshot.docs) {
          // プロジェクト数を集計
          const projectsRef = collection(db, `users/${userDoc.id}/projects`);
          const projectsSnapshot = await getDocs(projectsRef);
          totalProjects += projectsSnapshot.size;
          
          // 稼働時間を集計（削除済みレコードを除外）
          const timestampsRef = collection(db, `users/${userDoc.id}/timestamps`);
          const timestampsSnapshot = await getDocs(timestampsRef);
          
          timestampsSnapshot.forEach((timestampDoc) => {
            const record = timestampDoc.data();
            if (record.status !== 'deleted' && record.totalMinutes) {
              totalWorkingMinutes += record.totalMinutes;
            }
          });
        }
        
        const totalWorkingHours = Math.round(totalWorkingMinutes / 60 * 10) / 10;
        
        setStats({
          totalUsers,
          activeUsers,
          freeUsers,
          proUsers,
          userGrowth,
          activeGrowth: 0, // TODO: 実装
          totalProjects,
          totalWorkingHours,
        });
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading };
};
